// Course-Header des Workspace (Designkonzept §1/§5.5): Titel, Status-Chip,
// Performanz-Level nach Audit-Lauf, Primär-CTA „Workshop jetzt prüfen",
// einklappbare Detail-Zeile (Zielgruppe, Format, Dauer, Modalität).
// Gold erscheint hier NUR als Mikro-Typografie (Eyebrow) + die eine
// ausklingende Gold-Gradient-Trennlinie (§3 Gold-Regel).

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { AnalysisReport, Course, WizardCatalog } from "@/api/client";
import CreditCost from "@/components/CreditCost";
import { useModalityLabels } from "@/lib/labels";

export default function WorkspaceHeader({
  course,
  latestReport,
  catalog,
  auditing,
  onRunAudit,
}: {
  course: Course;
  latestReport: AnalysisReport | null;
  catalog: WizardCatalog | null;
  auditing: boolean;
  onRunAudit: () => void;
}) {
  const { t } = useTranslation();
  const modalityLabels = useModalityLabels();
  const [collapsed, setCollapsed] = useState(false);

  const formatLabel =
    catalog?.course_formats.find((f) => f.id === course.format)?.name ??
    course.format;

  return (
    <header
      className="bg-ws-panel px-6 py-3.5"
      style={{
        // Die EINE Gold-Linien-Situation: ausklingender Gradient als Border
        borderBottom: "1px solid transparent",
        borderImage:
          "linear-gradient(90deg, rgba(184,150,74,.55), rgba(184,150,74,.12) 40%, rgba(245,240,229,.07) 75%) 1",
      }}
    >
      {/* Eyebrow: Gold nur als Mikro-Typo, Versalien, gesperrt, Kurzstrich */}
      <div className="mb-1.5 flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-ws-gold-warm">
        <span className="inline-block h-px w-[26px] bg-ws-gold" aria-hidden />
        BRAIN-HR · DidaktikAligner
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <h1 className="text-[19px] font-semibold tracking-[-0.02em]">
          {course.title}
        </h1>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
            course.status === "in_review"
              ? "border-[rgba(77,124,254,.45)] text-ca-surface-accent bg-ws-card"
              : "border-ws-surface-border text-ws-surface-muted bg-ws-card"
          }`}
        >
          ● {t(`workspace.status.${course.status}`)}
        </span>
        {latestReport?.performance_level != null && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(77,124,254,.45)] bg-ws-card px-2.5 py-0.5 text-xs font-semibold text-ws-surface-ink">
            {t("workspace.perfLevel")}{" "}
            <b className="text-ca-surface-accent">{latestReport.performance_level} / 4</b>
          </span>
        )}
        <div className="ml-auto flex items-center gap-3">
          {auditing && (
            <span className="text-xs text-ws-text-3">
              {t("workspace.auditRunningHint")}
            </span>
          )}
          <CreditCost feature="audit" className="text-xs text-ws-text-3" />
          <button
            onClick={onRunAudit}
            disabled={auditing}
            className="inline-flex items-center gap-2 rounded-lg text-[13.5px] font-semibold tracking-[-0.01em] text-white shadow-ws-glow transition hover:brightness-110 disabled:opacity-60"
            style={{
              background: "linear-gradient(135deg, #4D7CFE, #7B5CFF)",
              padding: "9px 18px",
            }}
          >
            {auditing && (
              <span
                className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white"
                aria-hidden
              />
            )}
            {auditing ? t("workspace.auditRunning") : t("workspace.auditCta")}
          </button>
        </div>
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="text-xs text-ws-text-3 transition hover:text-ws-text-2"
        >
          {t("workspace.collapseDetails")}
        </button>
        <Link
          to={`/courses/${course.id}`}
          className="text-xs text-ws-text-3 transition hover:text-ws-glow"
        >
          {t("workspace.overviewLink")} →
        </Link>
      </div>
      {!collapsed && (
        <div className="mt-3 flex flex-wrap gap-7">
          {course.audience && (
            <HeaderItem label={t("workspace.detail.audience")} value={course.audience} />
          )}
          {formatLabel && (
            <HeaderItem label={t("workspace.detail.format")} value={formatLabel} />
          )}
          {course.duration_hours != null && (
            <HeaderItem
              label={t("workspace.detail.duration")}
              value={t("workspace.detail.hoursValue", { hours: course.duration_hours })}
            />
          )}
          {course.modality && (
            <HeaderItem
              label={t("workspace.detail.modality")}
              value={modalityLabels[course.modality] ?? course.modality}
            />
          )}
          {latestReport && (
            <HeaderItem
              label={t("workspace.detail.lastAudit")}
              value={new Date(latestReport.generated_at).toLocaleDateString(
                undefined,
                { day: "2-digit", month: "2-digit", year: "2-digit" },
              )}
            />
          )}
        </div>
      )}
    </header>
  );
}

function HeaderItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-ws-text-3">
        {label}
      </span>
      <span className="text-[13.5px] text-ws-text">{value}</span>
    </div>
  );
}
